/**
 * useMouseHalo - mouse-follow radial gradient halo via CSS variables.
 *
 * MD-31 ASKLAW-EMBER-THEME Phase 1.5 BOOST (2026-05-27, effect #7).
 *
 * Pattern (pure CSS-var + rAF, KHONG GSAP):
 *  - Listen mousemove tren ref element.
 *  - Throttle voi requestAnimationFrame (1 update per frame max).
 *  - Set --mouse-x + --mouse-y CSS var (% relative to rect).
 *  - CSS rule (.hero-ember::after) radial-gradient consume var.
 *
 * Guard skip (low-end or no-hover):
 *  - prefers-reduced-motion: reduce
 *  - (hover: none) -> touch device
 *  - navigator.deviceMemory <= 4 (low-end mobile/laptop)
 *  - matchMedia('(max-width: 720px)') -> mobile breakpoint
 *
 * Spec: F-FE-THEME-GEMINI.md §6.5.4 (transform-only/CSS var perf rule).
 *
 * Cleanup: removeEventListener mousemove + cancelAnimationFrame.
 */

import { useEffect, useRef } from 'react';

export function useMouseHalo<T extends HTMLElement = HTMLElement>() {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!ref.current) return;

    // Guard array - any TRUE skips the effect.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (window.matchMedia('(hover: none)').matches) return;
    if (window.matchMedia('(max-width: 720px)').matches) return;
    const nav = navigator as Navigator & { deviceMemory?: number };
    if (typeof nav.deviceMemory === 'number' && nav.deviceMemory <= 4) return;

    const el = ref.current;
    let raf = 0;
    let nextX = 50;
    let nextY = 0;

    const handler = (e: MouseEvent) => {
      const rect = el.getBoundingClientRect();
      nextX = ((e.clientX - rect.left) / rect.width) * 100;
      nextY = ((e.clientY - rect.top) / rect.height) * 100;
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        el.style.setProperty('--mouse-x', `${nextX}%`);
        el.style.setProperty('--mouse-y', `${nextY}%`);
      });
    };
    const leave = () => {
      if (raf) {
        cancelAnimationFrame(raf);
        raf = 0;
      }
      // Reset to default (center top) - CSS transitions 200ms ease back.
      el.style.setProperty('--mouse-x', '50%');
      el.style.setProperty('--mouse-y', '0%');
    };

    el.addEventListener('mousemove', handler, { passive: true });
    el.addEventListener('mouseleave', leave, { passive: true });

    return () => {
      el.removeEventListener('mousemove', handler);
      el.removeEventListener('mouseleave', leave);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return ref;
}
